import { showToast } from "@certn/cascadia";
import { Link } from "@certn/cascadia-react";
import {
  ActivityLogTab as SharedActivityLogTab,
  type ActivityLogFilterState,
} from "@certn/monitoring";
import { sortDescriptorToOrderingParam } from "@certn/cascadia-react/components/data-tables/utils";
import { useMemo, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { useTranslation } from "react-i18next";

import type {
  MonitoringActivityLogResponse,
  MonitoringActivityLogRun,
} from "../types";
import {
  downloadMonitoringActivityLogCsv,
  useMonitoringActivityLog,
  useMonitoringApplicantDetail,
} from "../useMonitoringApi";

const PAGE_SIZE = 25;

const EMPTY_FILTERS: ActivityLogFilterState = {
  search: "",
  checkTypes: [],
  monitorStatuses: [],
  resultStatuses: [],
  dateRange: null,
};

function buildActivityLogQuery(
  page: number,
  filters: ActivityLogFilterState,
  ordering: string | undefined
): Record<string, string | string[]> {
  return {
    page: String(page),
    page_size: String(PAGE_SIZE),
    ...(filters.search.trim() && { search: filters.search.trim() }),
    ...(filters.checkTypes.length && { check_type: filters.checkTypes }),
    ...(filters.monitorStatuses.length && {
      monitor_status: filters.monitorStatuses,
    }),
    ...(filters.resultStatuses.length && {
      result_status: filters.resultStatuses,
    }),
    ...(filters.dateRange && { date_range: filters.dateRange }),
    ...(ordering && { ordering }),
  };
}

export function ActivityLogTab({
  subjectId,
}: {
  subjectId: string | undefined;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ActivityLogFilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "run_date",
    direction: "descending",
  });

  const { data: rawDetail } = useMonitoringApplicantDetail(subjectId);
  const enrollments = rawDetail?.enrollments ?? [];

  const ordering = sortDescriptorToOrderingParam(sortDescriptor);
  const query = useMemo(
    () => buildActivityLogQuery(page, filters, ordering),
    [filters, ordering, page]
  );

  const { data: rawData, isLoading } = useMonitoringActivityLog(
    subjectId,
    query as Record<string, string>
  );
  const data = rawData as unknown as MonitoringActivityLogResponse | undefined;
  const rows = (data?.results ?? []) as MonitoringActivityLogRun[];

  async function handleExport() {
    if (!subjectId) {
      return;
    }
    setIsExporting(true);
    try {
      const { page: _page, page_size: _pageSize, ...exportQuery } = query;
      await downloadMonitoringActivityLogCsv(subjectId, exportQuery);
    } catch {
      showToast(t("Unable to download activity log CSV"), { type: "error" });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <SharedActivityLogTab
      rows={rows}
      count={data?.count ?? 0}
      isLoading={isLoading}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      filters={filters}
      onFiltersChange={setFilters}
      onResetPage={() => setPage(1)}
      checkOptions={enrollments}
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      onExport={handleExport}
      isExporting={isExporting}
      renderCaseLink={(shortId) => (
        <Link
          href={`/case-details/${shortId}`}
          testID="activity-log-monitor-case-link"
        >
          {shortId}
        </Link>
      )}
    />
  );
}

export {
  ActivityLogFilters,
  type ActivityLogFilterState,
} from "@certn/monitoring";
